import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import {
  VSpacer,
  HSpacer,
  Label,
  Text,
  LoadingOverlay,
  Button,
  Toggle,
  PhoneInput,
  CountrySelect,
  Input,
  Select,
  Tooltip,
  Icon,
} from '@mqd/volt-base';
import businessDirectorStore from '../stores/BusinessDirectorStore';

class BusinessDirectorDetails extends Component {
  constructor(props) {
    super(props);
    this.handleRevealIdentificationsButtonClick =
      this.handleRevealIdentificationsButtonClick.bind(this);
    this.handleHideIdentificationsButtonClick =
      this.handleHideIdentificationsButtonClick.bind(this);
    this.state = {
      resetLoading: false,
      resetError: [],
      showIdentifications: false,
      retrieveIdentificationForEditForm: true,
    };
  }

  handleRevealIdentificationsButtonClick() {
    const { businessDirectorStore } = this.props;
    const { business_token } = businessDirectorStore;

    businessDirectorStore.getDirectorIdentifications(business_token).then((success) => {
      this.setState({ showIdentifications: true });
    });
  }

  renderRevealIdentificationsButton() {
    return (
      <Button
        type="outline"
        onClick={() => {
          this.handleRevealIdentificationsButtonClick();
        }}
      >
        Reveal identifications
      </Button>
    );
  }

  handleHideIdentificationsButtonClick() {
    this.setState({ showIdentifications: false });
  }

  renderHideIdentificationsButton() {
    return this.state.showIdentifications ? (
      <Button
        type="outline"
        onClick={() => {
          this.handleHideIdentificationsButtonClick();
        }}
      >
        Hide identifications
      </Button>
    ) : null;
  }

  renderViewIdentificationFields() {
    const { businessDirectorStore } = this.props;
    const identificationObj = businessDirectorStore.identifications
      ? businessDirectorStore.identifications[0]
      : {};
    const identificationsFieldMap = {
      'Identification Type': identificationObj?.type,
      'Identification Value': identificationObj?.value,
    };

    return this.state.showIdentifications
      ? Object.keys(identificationsFieldMap).map((field) => {
          return this.renderViewGenerator(field, identificationsFieldMap[field]);
        })
      : null;
  }

  handleViewTextValues(field, storeValue) {
    switch (field) {
      case 'Status':
        const status = storeValue === true ? 'Active' : 'Inactive';
        return (
          <Label
            style={{ padding: 0, width: '64px', textAlign: 'center' }}
            type={status === 'Active' ? 'success' : 'gray'}
          >
            {status === 'Active' ? 'Active' : 'Inactive'}
          </Label>
        );
      case 'Address':
        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Text>{storeValue?.displayUS || ''}</Text>
          </div>
        );
      case 'Identifications':
        return !this.state.showIdentifications ? this.renderRevealIdentificationsButton() : null;
      default:
        return <Text>{storeValue}</Text>;
    }
  }

  renderViewGenerator(field, storeValue) {
    return (
      <React.Fragment>
        <div
          style={{ width: '100%', display: 'flex' }}
          data-testid={`business-director-details-drilldown-field_${field}`}
        >
          <div style={{ width: '50%' }}>
            <Text type="h6">{field}</Text>
          </div>
          <div
            style={{
              width: '50%',
              overflowWrap: 'break-word',
              maxHeight: '120px',
              overflowY: 'auto',
            }}
          >
            {this.handleViewTextValues(field, storeValue)}
          </div>
        </div>
        <VSpacer factor={3} />
      </React.Fragment>
    );
  }

  renderViewForm() {
    const { businessDirectorStore, canRevealIdentification } = this.props;
    const fieldMap = {
      'First name': businessDirectorStore.first_name,
      'Middle name': businessDirectorStore.middle_name,
      'Last name': businessDirectorStore.last_name,
      Status: businessDirectorStore.active,
      Token: businessDirectorStore.token,
      Title: businessDirectorStore.title,
      Email: businessDirectorStore.email,
      Phone: businessDirectorStore.phone,
      'Birth date': businessDirectorStore.birth_date,
      'Place of birth': businessDirectorStore.place_of_birth,
      Nationality: businessDirectorStore.nationality,
      Address: businessDirectorStore.address,
    };
    return (
      <>
        <div
          style={{ display: 'flex', flexDirection: 'column' }}
          className="business-director-details-drilldown-view__container"
        >
          {Object.keys(fieldMap).map((field) => {
            return this.renderViewGenerator(field, fieldMap[field]);
          })}
          {canRevealIdentification && (
            <div>
              {this.renderViewGenerator('Identifications', '')}
              {this.renderViewIdentificationFields()}
              {this.renderHideIdentificationsButton()}
            </div>
          )}
        </div>
        <div style={{ borderBottom: '1px solid #DFE0E1' }}>
          <VSpacer />
        </div>
        <VSpacer factor={3} />
      </>
    );
  }

  // Edit mode methods
  renderEditForm() {
    const { businessDirectorStore, canEditIdentification } = this.props;
    const { retrieveIdentificationForEditForm } = this.state;

    //if user can reveal/edit identification and identifications are not loaded yet
    //then fetch identifications for the edit form
    if (canEditIdentification && retrieveIdentificationForEditForm) {
      if (
        !businessDirectorStore.identifications ||
        businessDirectorStore.identifications.length === 0
      ) {
        businessDirectorStore.getDirectorIdentifications(businessDirectorStore.business_token);
        // Set state to false to prevent multiple fetches
        this.setState({ retrieveIdentificationForEditForm: false });
      }
    }

    return (
      <>
        <Input
          preventErrorPushdown
          label="First Name"
          placeholder="Enter First Name"
          onChange={(event) => businessDirectorStore.setAttr('first_name', event.target.value)}
          value={businessDirectorStore.first_name}
        />
        <VSpacer factor={3} />
        <Input
          preventErrorPushdown
          label="Middle Name"
          placeholder="Enter Middle Name"
          onChange={(event) => businessDirectorStore.setAttr('middle_name', event.target.value)}
          value={businessDirectorStore.middle_name}
        />
        <VSpacer factor={3} />
        <Input
          preventErrorPushdown
          label="Last Name"
          placeholder="Enter Last Name"
          onChange={(event) => businessDirectorStore.setAttr('last_name', event.target.value)}
          value={businessDirectorStore.last_name}
        />
        <VSpacer factor={3} />
        <Toggle
          label="Status"
          active={businessDirectorStore.active}
          onClick={() => businessDirectorStore.setAttr('active', !businessDirectorStore.active)}
        >
          Active
        </Toggle>
        <VSpacer factor={3} />
        <Input
          preventErrorPushdown
          label="Title"
          placeholder="Enter Job Title"
          onChange={(event) => businessDirectorStore.setAttr('title', event.target.value)}
          value={businessDirectorStore.title}
        />
        <VSpacer factor={3} />
        <Input
          preventErrorPushdown
          label="Email"
          placeholder="Enter Email"
          onChange={(event) => businessDirectorStore.setAttr('email', event.target.value)}
          value={businessDirectorStore.email}
        />
        <VSpacer factor={3} />
        <PhoneInput
          value={businessDirectorStore.phone}
          width="312"
          label="Phone"
          id="director-phone-input"
          onChange={(value) => businessDirectorStore.setAttr('phone', value)}
        />
        <VSpacer factor={3} />
        <Input
          preventErrorPushdown
          label="Birth Date"
          placeholder="Enter Birth Date (YYYY-MM-DD)"
          onChange={(event) => businessDirectorStore.setAttr('birth_date', event.target.value)}
          value={businessDirectorStore.birth_date}
        />
        <VSpacer factor={3} />
        <CountrySelect
          label="Place of Birth"
          placeholder="Select country/region of place of birth"
          onChange={(option) => {
            const { alpha2: country } = option;
            businessDirectorStore.setAttr('place_of_birth', country);
          }}
          width={'335px'}
          initialValue={businessDirectorStore.place_of_birth || null}
        />
        <VSpacer factor={3} />
        <CountrySelect
          label="Nationality"
          placeholder="Select country/region of nationality"
          onChange={(option) => {
            const { alpha2: country } = option;
            businessDirectorStore.setAttr('nationality', country);
          }}
          width={'335px'}
          initialValue={businessDirectorStore.nationality || null}
        />
        <VSpacer factor={3} />
        <Input
          preventErrorPushdown
          label="Address Line 1"
          placeholder="Enter Address Line 1 of Residence"
          onChange={(event) => {
            const value = event.target.value;
            businessDirectorStore.setAttr('address', {
              ...businessDirectorStore.address,
              address1: value,
            });
          }}
          value={businessDirectorStore.address?.address1}
        />
        <VSpacer factor={3} />
        <Input
          preventErrorPushdown
          label="Address Line 2"
          placeholder="Enter Address Line 2 of Residence"
          onChange={(event) => {
            const value = event.target.value;
            businessDirectorStore.setAttr('address', {
              ...businessDirectorStore.address,
              address2: value,
            });
          }}
          value={businessDirectorStore.address?.address2}
        />
        <VSpacer factor={3} />
        <Input
          preventErrorPushdown
          label="City"
          placeholder="Enter City of Residence"
          onChange={(event) => {
            const value = event.target.value;
            businessDirectorStore.setAttr('address', {
              ...businessDirectorStore.address,
              city: value,
            });
          }}
          value={businessDirectorStore.address?.city}
        />
        <VSpacer factor={3} />
        <Input
          preventErrorPushdown
          label="State"
          placeholder="Enter State of Residence"
          onChange={(event) => {
            const value = event.target.value;
            businessDirectorStore.setAttr('address', {
              ...businessDirectorStore.address,
              state: value,
            });
          }}
          value={businessDirectorStore.address?.state}
        />
        <VSpacer factor={3} />
        <CountrySelect
          label="Country"
          placeholder="Select country/region of residence"
          onChange={(option) => {
            const { alpha2: country } = option;
            businessDirectorStore.setAttr('address', {
              ...businessDirectorStore.address,
              country,
            });
          }}
          width={'335px'}
          initialValue={businessDirectorStore.address?.country}
        />
        <VSpacer factor={3} />
        <Input
          preventErrorPushdown
          label="Postal Code / ZIP Code"
          placeholder="Enter Postal Code or ZIP code of Residence"
          onChange={(event) => {
            const value = event.target.value;
            businessDirectorStore.setAttr('address', {
              ...businessDirectorStore.address,
              postal_code: value,
              zip: value,
            });
          }}
          value={businessDirectorStore.address?.postal_code || businessDirectorStore.address?.zip}
        />
        <VSpacer factor={3} />
        {canEditIdentification && (
          <div>
            <Select
              label="Identification Type"
              placeholder="Select Identification Type"
              options={['SSN', 'TIN', 'SIN', 'NIN']}
              fixOverflow={true}
              value={businessDirectorStore.identificationType?.toUpperCase() || null}
              onChange={(value) => {
                businessDirectorStore.setAttr('identificationHasBeenUpdated', true);
                businessDirectorStore.setAttr('identificationType', value);
              }}
            />
            <VSpacer factor={3} />
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Text type="h6">Identification Value</Text>
              <HSpacer factor={0.5} />
              <div style={{ display: 'inline' }}>
                <Tooltip
                  portalZIndex={9999}
                  content={`
                      Forms of identification used to validate the business director. SSN values need to be 9 digits without dashes or spaces.
                    `}
                  width={'300px'}
                  fixOverflow={true}
                  testId="business-director-edit-identification-tooltip"
                >
                  <Icon type="info" mod="disabled" hasTooltip />
                </Tooltip>
              </div>
            </div>
            <Input
              preventErrorPushdown
              placeholder="Enter Identification Value"
              onChange={(event) => {
                businessDirectorStore.setAttr('identificationHasBeenUpdated', true);
                businessDirectorStore.setAttr('identificationValue', event.target.value);
              }}
              value={businessDirectorStore.identificationValue}
            />
          </div>
        )}
      </>
    );
  }

  render() {
    const { businessDirectorStore, editDirectorActive } = this.props;
    const shouldShowLoading = !businessDirectorStore?.token;

    return (
      <LoadingOverlay active={shouldShowLoading} testId="business-director-details-loading">
        {editDirectorActive ? this.renderEditForm() : this.renderViewForm()}
      </LoadingOverlay>
    );
  }
}

BusinessDirectorDetails.propTypes = {
  businessDirectorStore: PropTypes.any,
  canEditDirectorDetails: PropTypes.bool,
  canRevealIdentification: PropTypes.bool,
  canEditIdentification: PropTypes.bool,
  editDirectorActive: PropTypes.bool,
};

BusinessDirectorDetails.defaultProps = {
  businessDirectorStore: new businessDirectorStore(),
  canEditDirectorDetails: false,
  canRevealIdentification: false,
  canEditIdentification: false,
  editDirectorActive: false,
};

export default observer(BusinessDirectorDetails);
